<template>
  <div class="homepage-search-vehicle__filters-section">
    <div
      class="homepage-search-vehicle__filters-section-content"
      :class="{ 'homepage-search-vehicle__filters-section-content--hidden': !sectionVisible }"
    >

      <div
        v-if="type === 'select'"
        class="form-input__select"
        v-click-outside="dropdownOpen"
      >
        <div class="form-input__input-with-arrow">
          <input
            :id="optionsKey"
            type="text"
            class="form-input form-input__input-primary-inverted dropdown-toggle form-input__select__input"
            :value="inputValue"
            :placeholder="`${placeholder}`"
            :readonly="!enableSearch"
            @focus="onInteract"
            @click="onInteract"
            @input="enableSearch && onInput($event)"
          />

          <!-- Dropdown arrow -->
          <span
            class="form-input__select__arrow"
            :class="{ open: dropdownOpen }"
            @click="dropdownOpen = !dropdownOpen"
          >▾</span>
        </div>

        <transition name="fade-slide">
          <div v-if="dropdownOpen" class="form-input__select__dropdown">
            <div v-if="loading" class="form-input__select__loading">
              <span class="spinner"></span>
            </div>
            <div
              v-else
              v-for="(option, index) in filteredOptions"
              :key="index"
              class="form-input__select__option"
              @click.prevent="selectOption(option.value)"
            >
              <label class="form-input__select__label">
                <input
                  v-if="multiple"
                  type="checkbox"
                  :value="option.value"
                  v-model="selectedValue"
                />
                <span class="form-input__select__option-text">{{ option.label }}</span>
                <span v-if="hasCount" class="form-input__select__count">{{ option.count }}</span>
              </label>
            </div>
          </div>
        </transition>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  emits: ['field:open'],
  props: {
    placeholder: { required: true },
    displayName: { required: false, default: ''},
    optionsKey: { required: true },
    type: { default: "checkbox" },
    multiple: { type: Boolean, default: false },
    hasCount: { type: Boolean, default: true },
    enableSearch: { type: Boolean, default: false },
    formatFunction: { default: null },
  },
  data() {
    return {
      sectionVisible: false,
      dropdownOpen: false,
      searchValue: "",
    };
  },
  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.__clickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression] = false;
          }
        };
        document.body.addEventListener("click", el.__clickOutsideHandler__);
      },
      unbind(el) {
        document.body.removeEventListener("click", el.__clickOutsideHandler__);
      },
    },
  },
  computed: {
    ...mapGetters({
      userOptions: "SearchOptions/userOptions",
      loading: "SearchOptions/loading",
    }),
    availableOptions() {
      if (this.optionsKey === "distance") {
        return this.$store.getters["SearchOptions/distances"].map((d) => ({
          value: d,
          label: this.formatFunction ? this.formatFunction(d) : d,
        }));
      }
      if (this.optionsKey === "maxPrice") {
        const prices = this.$store.getters["SearchOptions/maxPrices"] || [];
        return prices.map(d => ({
          value: d.price,
          label: this.formatFunction
            ? this.formatFunction(d.price)
            : d.price,
          disabled: d.disabled
        }));
      }
      const options = this.$store.state.SearchOptions.options[this.optionsKey] || [];
      return options.map((opt) => ({
        value: opt[this.optionsKey] ?? opt,
        label: this.formatFunction
          ? this.formatFunction(opt[this.optionsKey] ?? opt)
          : opt[this.optionsKey] ?? opt,
        count: opt.count ?? null,
      }));
    },
    filteredOptions() {
      if (!this.enableSearch) return this.availableOptions;

      if (!this.searchValue || (!this.multiple && this.selectedValue === this.searchValue)) {
        return this.availableOptions;
      }

      const search = this.searchValue.toLowerCase();
      return this.availableOptions.filter((opt) =>
        opt.label.toLowerCase().includes(search)
      );
    },
    selectedValue: {
      get() {
        const val = this.userOptions[this.optionsKey];
        return this.multiple ? (Array.isArray(val) ? val : []) : val || "";
      },
      set(value) {
        this.$store.dispatch("SearchOptions/updateOptions", {
          [this.optionsKey]: value,
        });
      }
    },
    selectedLabel() {
      if (this.multiple) return "";
      const option = this.availableOptions.find(opt => opt.value === this.selectedValue);
      return option ? option.label : "";
    },
    inputValue() {
      if (this.multiple) {
        const selected = this.selectedValue;
        if (!selected || selected.length === 0) return null;
        // display multiselected
        if (selected.length <= 3) {
          return this.availableOptions
            .filter(opt => selected.includes(opt.value))
            .map(opt => opt.label)
            .join(', ');
        }
        return `${selected.length} ${this.displayName.toLowerCase()} selected`;
      } else {
        return this.searchValue || this.selectedLabel || null;
      }
    }
  },
  methods: {
    onInteract() {
      this.dropdownOpen = true;
      this.$emit('field:open', this.optionsKey);
    },
    onInput(event) {
      this.searchValue = event.target.value;
      this.dropdownOpen = true;
    },
    selectOption(value) {
        if (this.multiple) {
            const idx = this.selectedValue.indexOf(value);
            if (idx > -1) this.selectedValue.splice(idx, 1);
            else this.selectedValue.push(value);
        } else {
            this.selectedValue = value;
            this.searchValue = "";
            this.dropdownOpen = false;
        }
        this.$store.dispatch("SearchOptions/getOptions");
    }
  },
};
</script>